import { mountRoute } from './mountRoute';

import type { AnyRoute } from '@tanstack/react-router';

import type { MountRouteContext, MountRouteOptions } from './mountRoute';
import type {
  DeclaredCrumbs,
  RouteLeavePolicy,
  SingleRootParity,
  SingleRootStaticData
} from './types';

/** The parity entry of a route that implements no legacy affordance and has no PartGap. */
export const NO_PARITY: SingleRootParity = { covers: [], missingParts: [] };

type PageProps<TProps extends object> = (context: MountRouteContext) => TProps;

interface AdminRouteFields<TProps extends object> extends Omit<
  MountRouteOptions<TProps>,
  'breadcrumbs' | 'leave' | 'parity' | 'props' | 'title'
> {
  title: NonNullable<SingleRootStaticData['title']>;
  /** Omitted: the served document's crumbs, else one crumb from the title. */
  crumbs?: DeclaredCrumbs;
  leave?: RouteLeavePolicy;
  parity?: SingleRootParity;
  props?: PageProps<TProps>;
}

/** `props` may be omitted only for a page that takes no required props. */
export type AdminRouteOptions<TProps extends object> = AdminRouteFields<TProps> &
  (object extends TProps ? unknown : { props: PageProps<TProps> });

/**
 * A page rebuilt for the shell, on `mountRoute`. It leaves client side unless it says otherwise;
 * an adopted legacy page that still needs native unload uses `mountRoute`, whose default is
 * `document`.
 */
export const adminRoute = <TProps extends object>(options: AdminRouteOptions<TProps>): AnyRoute => {
  const {
    crumbs,
    leave = 'client',
    parity = NO_PARITY,
    props = () => ({}) as TProps,
    ...page
  }: AdminRouteFields<TProps> = options;
  return mountRoute<TProps>({ ...page, breadcrumbs: crumbs, leave, parity, props });
};
